/**
 * Accès d'un employé à l'application.
 *
 * L'écran a longtemps proposé un « code d'invitation » tiré dans le navigateur
 * qu'aucun point d'entrée du serveur ne connaissait, puis un compte à mot de
 * passe provisoire que le patron recopiait dans un message. L'invitation est
 * désormais un lien que le serveur tire et que l'employé suit : il choisit
 * lui-même son code. Ces quelques règles préparent ce que l'écran en montre, et
 * se vérifient seules.
 */

/** Rôles qu'un patron peut attribuer, et ce qu'ils ouvrent. */
export const ROLES = [
  { cle: 'OUVRIER', nom: 'Ouvrier', quoi: 'Son espace : pointages, feuilles, salaire' },
  { cle: 'TECHNICIEN', nom: 'Chef d’équipe', quoi: 'Chantiers, devis, planning' },
  { cle: 'SECRETAIRE', nom: 'Secrétariat', quoi: 'Clients, documents, agenda' },
  { cle: 'COMPTABLE', nom: 'Comptable', quoi: 'Documents commerciaux et comptabilité' },
  {
    cle: 'DIRECTEUR',
    nom: 'Directeur des travaux',
    quoi: 'Toutes les équipes et tous les chantiers ; le reste, tu le règles',
  },
  { cle: 'PATRON', nom: 'Direction', quoi: 'Tout, y compris salaires et réglages' },
];

/**
 * Nom complet d'un employé, tel que le compte le portera.
 *
 * @param {object} [employe] fiche de l'employé
 * @returns {string} le nom, ou une chaîne vide
 */
export function nomComplet(employe) {
  return [employe?.prenom, employe?.nom].filter(Boolean).join(' ').trim();
}

/**
 * Message affiché tant que la fiche n'a pas reçu son identifiant du serveur.
 *
 * Un clic sur « Inviter » juste après la création de la fiche partait avant
 * qu'elle ne soit synchronisée : l'invitation naissait sans fiche. Le bouton
 * attend, et le dit.
 */
export const MESSAGE_FICHE_NON_ENREGISTREE =
  'Enregistrement de la fiche en cours… L’invitation sera possible dans un instant.';

/**
 * Dit si la fiche est enregistrée au serveur, condition pour y rattacher un
 * accès : seule la base attribue des identifiants numériques, l'écran des
 * identifiants de travail provisoires.
 *
 * @param {object} [employe] fiche de l'employé
 * @returns {boolean} vrai si la fiche porte un identifiant du serveur
 */
export function ficheEnregistree(employe) {
  return typeof employe?.id === 'number' && Number.isFinite(employe.id);
}

/**
 * Dit si un compte est le compte technique ouvert par le code PIN, et non un
 * accès par e-mail : son adresse (`ouvrier-…@local`) n'en est pas une.
 *
 * @param {object} [compte] compte renvoyé par le serveur
 * @returns {boolean} vrai pour le compte du code PIN
 */
export function estAccesParPin(compte) {
  if (!compte) return false;
  if (typeof compte.accesParPin === 'boolean') return compte.accesParPin;
  return /^ouvrier-.*@local$/.test(String(compte.email || ''));
}

/**
 * Date et heure d'un instant du serveur, telles qu'on les écrit en Suisse.
 *
 * @param {string} iso instant ISO 8601
 * @returns {string} `JJ.MM.AAAA à HH:MM`, ou une chaîne vide
 */
export function dateHeureSuisse(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const deux = (n) => String(n).padStart(2, '0');
  return (
    `${deux(d.getDate())}.${deux(d.getMonth() + 1)}.${d.getFullYear()}` +
    ` à ${deux(d.getHours())}:${deux(d.getMinutes())}`
  );
}

/**
 * Ce que la fiche dit de la dernière invitation.
 *
 * @param {object} [invitation] invitation rendue par le serveur
 * @returns {{etat: string, libelle: string, enAttente: boolean}|null} le libellé,
 *   ou `null` sans invitation
 */
export function libelleInvitation(invitation) {
  if (!invitation?.etat) return null;
  switch (invitation.etat) {
    case 'EN_ATTENTE':
      return {
        etat: 'EN_ATTENTE',
        enAttente: true,
        libelle:
          'Invitation envoyée le ' +
          dateHeureSuisse(invitation.creeeLe) +
          (invitation.expireLe
            ? ' · valable jusqu’au ' + dateHeureSuisse(invitation.expireLe)
            : ''),
      };
    case 'ACCEPTEE':
      return {
        etat: 'ACCEPTEE',
        enAttente: false,
        libelle: 'Invitation acceptée le ' + dateHeureSuisse(invitation.accepteeLe),
      };
    case 'EXPIREE':
      return {
        etat: 'EXPIREE',
        enAttente: false,
        libelle: 'Invitation expirée le ' + dateHeureSuisse(invitation.expireLe),
      };
    case 'ANNULEE':
      return {
        etat: 'ANNULEE',
        enAttente: false,
        libelle: 'Invitation annulée le ' + dateHeureSuisse(invitation.revoqueeLe),
      };
    default:
      return { etat: invitation.etat, enAttente: false, libelle: 'Invitation' };
  }
}
